import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AssignDeviceToOrganization,
  BulkAssignDevicesToOrganization,
  DownloadDeviceDataFormat
} from '../../../network/Organization';
import { DeviceInventoryList } from '../../../network/Devices';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '30%',
  maxWidth: '35%',
  bgcolor: 'background.paper',
  p: 2,
  borderRadius: 1,
  minHeight: '300px',
  maxHeight: '90vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column'
};
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const useStyles = makeStyles((theme) => ({
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  }
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

export default function OrganizationDeviceAssignModal({ openAssignedModal, handleClose, orgData }) {
  const classes = useStyles();
  const [allDevices, setAllDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState([]);
  const [loader, setLoader] = useState(false);
  const [bulkUploadLoader, setBulkUploadLoader] = useState(false);
  const [alertMetas, setAlertMetas] = useState({
    snackBarOpened: false,
    msg: '',
    variant: 'filled',
    severity: ''
  });

  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertMetas({ ...alertMetas, snackBarOpened: false });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('organizationId', orgData?.id);
      formData.append('file', file);

      try {
        setBulkUploadLoader(true);
        const res = await BulkAssignDevicesToOrganization(formData);

        if (res) {
          setBulkUploadLoader(false);
          setAlertMetas({
            ...alertMetas,
            severity: 'success',
            snackBarOpened: true,
            msg: 'Device Assigned Successfully !!'
          });
        }

        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      } catch (error) {
        setBulkUploadLoader(false);
        setAlertMetas({
          ...alertMetas,
          severity: 'error',
          snackBarOpened: true,
          msg: error?.response?.data?.validationErrors?.message
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      }
    }
  };

  const allDeviceList = async () => {
    try {
      const res = await DeviceInventoryList();
      if (res) {
        setAllDevices(res?.data?.data?.deviceInventory);
      }
    } catch (error) {
      console.log(error, 'err');
    }
  };

  const assignDevice = async () => {
    setLoader(true);

    const payload = {
      organizationId: orgData?.id,
      deviceIds: selectedDevice?.map((item) => item?.device_id)
    };

    try {
      const res = await AssignDeviceToOrganization(payload);
      if (res) {
        setAlertMetas({
          snackBarOpened: true,
          severity: 'success',
          msg: 'Device Assigned Successfully !!',
          variant: 'filled'
        });
        handleClose();
      }
    } catch (error) {
      setAlertMetas({
        snackBarOpened: true,
        severity: 'error',
        msg: error?.response?.data?.message || 'Failed to assign devices',
        variant: 'filled'
      });
    } finally {
      setLoader(false);
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false }), 4000);
    }
  };

  const downloadFormat = async () => {
    try {
      const response = await DownloadDeviceDataFormat();

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      });

      const contentDisposition = response.headers['content-disposition'];
      const fileName =
        contentDisposition && contentDisposition.includes('filename=')
          ? contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '')
          : 'Device_Data_Sheet_Format.xlsx';

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);

      setAlertMetas({
        snackBarOpened: true,
        severity: 'success',
        msg: 'Format Downloaded Successfully !!',
        variant: 'filled'
      });
    } catch (error) {
      setAlertMetas({
        snackBarOpened: true,
        severity: 'error',
        msg: error?.response?.data?.message || 'Something went wrong!',
        variant: 'filled'
      });
    }
  };

  useEffect(() => {
    allDeviceList();
  }, []);

  return (
    <Box>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={alertMetas.snackBarOpened}
        onClose={handleSnackBarClose}
        autoHideDuration={6000}
      >
        <Alert
          onClose={handleSnackBarClose}
          severity={alertMetas.severity}
          variant={alertMetas.variant}
        >
          {alertMetas.msg}
        </Alert>
      </Snackbar>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="organisation-device-assign"
      >
        <Box sx={style}>
          <Typography sx={{ fontSize: 18, fontWeight: 900, mb: 2 }}>
            Assign Devices to Organization
          </Typography>
          <Link
            sx={{
              fontSize: { xs: '12px', md: '14px', marginBottom: '16px' },
              lineHeight: '16px',
              fontWeight: '600',
              color: 'blue',
              cursor: 'pointer'
            }}
            onClick={downloadFormat}
            target="_blank"
          >
            Device Data Sheet Format
          </Link>

          <FormControl fullWidth>
            <Autocomplete
              multiple
              id="device_search"
              options={allDevices || []}
              getOptionLabel={(device) => device?.device_id || ''}
              filterSelectedOptions
              value={selectedDevice}
              onChange={(event, newValue) => setSelectedDevice(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Devices" placeholder="Search for devices" />
              )}
              popupIcon={<ExpandMoreIcon />}
              renderTags={(selected, getTagProps) =>
                selected?.map((option, index) => (
                  <Chip
                    key={index}
                    label={option?.device_id}
                    {...getTagProps({ index })}
                    color="primary"
                  />
                ))
              }
              renderOption={(props, option) => (
                <MenuItem key={option?.imei} value={option} {...props} className={classes.menuItem}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: '100%' }}
                  >
                    {option?.device_id}
                    {selectedDevice?.includes(option) ? (
                      <IconButton aria-label="remove" color="error">
                        <RemoveOutlinedIcon />
                      </IconButton>
                    ) : (
                      <IconButton aria-label="add" color="success">
                        <AddOutlinedIcon />
                      </IconButton>
                    )}
                  </Stack>
                </MenuItem>
              )}
            />
            {/* <Select
              size="large"
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              multiple
              value={selectedDevice}
              onChange={handleChange}
              input={<OutlinedInput label="Select Devices" />}
              renderValue={(selected) =>
                selected
                  .map((id) => allDevices.find((device) => device.device_id === id)?.device_id)
                  .join(', ')
              }
              MenuProps={MenuProps}
            >
              {allDevices?.map((item) => (
                <MenuItem key={item.device_id} value={item.device_id}>
                  <Checkbox checked={selectedDevice?.indexOf(item?.device_id) > -1} />
                  <ListItemText primary={item.device_id} />
                </MenuItem>
              ))}
            </Select> */}
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 'auto' }}>
            <LoadingButton
              size="small"
              type="button"
              variant="contained"
              color="success"
              component="label"
              role={undefined}
              loading={bulkUploadLoader}
              sx={{ color: '#ffffff' }}
            >
              Bulk Upload
              <VisuallyHiddenInput
                data-testid="bulk-file-upload-input"
                type="file"
                onChange={handleFileUpload}
              />
            </LoadingButton>
            <Box>
              <Button
                size="small"
                type="button"
                variant="outlined"
                sx={{ mr: 2 }}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                type="submit"
                variant="contained"
                loading={loader}
                onClick={assignDevice}
              >
                Assign
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
